import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import {
  PretCourtoisieForm, CourtoisieDocsGrid,
  usePretFichiersBuffer, initPret, CARBURANT_LABEL,
} from './shared/PretCourtoisieForm';
import './VehiculesCourtoisie.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';

import { texteTheme } from '../utils/theme';
/* ─────────────────────────────────────────────────────────────────────────── */
/* Utilitaires                                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */

const normaliserImmat = (s) => (s || '').toUpperCase().replace(/[\s-]/g, '');

/* ─────────────────────────────────────────────────────────────────────────── */
/* Pièces jointes d'un prêt, permis de conduire + OCR IA, formulaire de prêt :  */
/* extraits dans shared/PretCourtoisieForm.js (réutilisés par l'onglet          */
/* Courtoisie de la modale d'intervention, Planning Atelier).                   */
/* ─────────────────────────────────────────────────────────────────────────── */
const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const STATUT_LABEL = { disponible: 'Disponible', en_pret: 'En prêt', maintenance: 'Maintenance', hors_service: 'Hors service' };
const STATUT_COLOR = { disponible: 'light-dark(#10b981, var(--color-success-dark))', en_pret: 'light-dark(#ef4444, var(--color-error-dark))', maintenance: '#f59e0b', hors_service: 'light-dark(#6b7280, var(--text-secondary))' };
const STATUT_BG    = { disponible: 'light-dark(#dcfce7, var(--color-success-light))', en_pret: 'light-dark(#fee2e2, var(--color-error-light))', maintenance: 'light-dark(#fef3c7, var(--color-warning-light))', hors_service: 'light-dark(#f1f5f9, var(--bg-muted))' };

// Statuts d'un prêt (différents des statuts véhicule de la flotte)
const STATUT_PRET = {
  en_cours: { label: 'En cours', color: 'light-dark(#b45309, var(--color-warning-dark))', bg: 'light-dark(#fef3c7, var(--color-warning-light))' },
  termine:  { label: 'Terminé',  color: 'light-dark(#166534, var(--color-success-dark))', bg: 'light-dark(#dcfce7, var(--color-success-light))' },
  annule:   { label: 'Annulé',   color: 'light-dark(#6b7280, var(--text-secondary))', bg: 'light-dark(#f1f5f9, var(--bg-muted))' },
};

const StatutBadge = ({ statut }) => (
  <span className="vc-statut-badge" style={{ color: STATUT_COLOR[statut] || '#64748b', background: STATUT_BG[statut] || '#f1f5f9' }}>
    {STATUT_LABEL[statut] || statut}
  </span>
);

/* ─────────────────────────────────────────────────────────────────────────── */
/* Valeurs initiales des formulaires                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

const initVehicule = { entite_juridique_id: '', concession_id: '', flotte_id: '', immatriculation: '', marque: '', modele: '', carburant: 'essence', boite_vitesse: 'BVM', km_max: '', date_limite: '' };
const initLoueur = { nom: '', concession_id: '' };

const CAUTION_LABEL = { cheque: 'Chèque', cb: 'Empreinte CB' };
const formatMontant = (n) => {
  if (n == null || n === '') return null;
  const v = Number(n);
  if (isNaN(v)) return null;
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};
const initRetour = { km_retour: '', date_retour: new Date().toISOString().split('T')[0], observations: '' };

/* ─────────────────────────────────────────────────────────────────────────── */
/* Composant principal                                                          */
/* ─────────────────────────────────────────────────────────────────────────── */

function VehiculesCourtoisie({ user, editMode = false, setHeaderAction }) {
  // Colonne Planification : dossier atelier du CLIENT qui a le vehicule de pret.
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadData(),
  });
  const [activeTab, setActiveTab]         = useState('prets');

  // Boutons d'action dans le header — changent selon l'onglet
  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'flotte') {
      setHeaderAction({
        primary: { label: '+ Ajouter un véhicule', onClick: () => openAjouterVehicule() },
        secondary: user?.role === 'super-admin' ? [
          { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Import</>, onClick: () => document.getElementById('vc-import-input')?.click() }
        ] : []
      });
    } else if (activeTab === 'loueurs') {
      setHeaderAction({
        primary: { label: '+ Ajouter un loueur', onClick: () => openAjouterLoueur() }
      });
    } else if (activeTab === 'archives') {
      // Onglet en lecture seule : aucun bouton d'action.
      setHeaderAction(null);
    } else {
      setHeaderAction({
        primary: { label: '+ Nouveau prêt Interne', onClick: () => handleNouveauPret(null, 'interne') },
        secondary: [
          { label: '+ Nouveau prêt Location', onClick: () => handleNouveauPret(null, 'location') }
        ]
      });
    }
  }, [activeTab, setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const [vehicules, setVehicules]         = useState([]);
  const [concessions, setConcessions]     = useState([]);
  const [entites, setEntites]             = useState([]);
  const [flottes, setFlottes]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  // Flotte
  const [filterConcession, setFilterConcession] = useState('all');
  const [filterStatutFlotte, setFilterStatutFlotte] = useState('all');
  const [searchFlotte, setSearchFlotte]     = useState('');
  const [showModalVehicule, setShowModalVehicule] = useState(false);
  const [editVehicule, setEditVehicule]     = useState(null); // null = création
  const [formVehicule, setFormVehicule]     = useState(initVehicule);
  const [savingVehicule, setSavingVehicule] = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importResult, setImportResult]     = useState(null); // { imported, errors }

  // Prêts
  const [viewMode, setViewMode]             = useState('tableau');
  const [filterStatutPret, setFilterStatutPret] = useState('all');
  const [searchPret, setSearchPret]         = useState('');
  const [currentWeek, setCurrentWeek]       = useState(0);
  const [showModal, setShowModal]           = useState(false);
  const [modalType, setModalType]           = useState(null);
  const [selectedVehicule, setSelectedVehicule] = useState(null);
  const [selectedPretId, setSelectedPretId] = useState(null);
  const [showHistory, setShowHistory]       = useState(false);
  const [historique, setHistorique]         = useState([]);
  const [formPret, setFormPret]             = useState(initPret);
  const [formRetour, setFormRetour]         = useState(initRetour);
  const [saving, setSaving]                 = useState(false);
  const [genPdf, setGenPdf]                 = useState(false);
  const [uploadingContrat, setUploadingContrat] = useState(false);
  const [editingPret, setEditingPret]       = useState(false);
  const [formEditPret, setFormEditPret]     = useState({});
  // Prêt : mode interne / location
  const [pretMode, setPretMode]             = useState('interne');
  // Partenaires loueurs
  const [loueurs, setLoueurs]               = useState([]);
  const [showModalLoueur, setShowModalLoueur] = useState(false);
  const [editLoueur, setEditLoueur]         = useState(null);
  const [formLoueur, setFormLoueur]         = useState(initLoueur);
  const [savingLoueur, setSavingLoueur]     = useState(false);
  // Pièces jointes du prêt (détail)
  const [fichiers, setFichiers]             = useState([]);
  const [uploadingFichier, setUploadingFichier] = useState(false);
  // Liste de TOUS les prêts (internes + locations) — alimente l'onglet Prêts
  const [prets, setPrets]                   = useState([]);
  // Modale « Documents » par prêt (réutilisable + enchaînement post-création)
  const [docPret, setDocPret]               = useState(null); // { id, label } | null
  // Pièces jointes bufferisées en mémoire le temps de la création d'un prêt
  // (il n'y a pas encore d'id pour les téléverser) — voir usePretFichiersBuffer.
  const { pretFichiersBuffer, addPretFichierBuffer, removePretFichierBuffer, clearPretFichiersBuffer } = usePretFichiersBuffer();

  /* ── Chargement données ─────────────────────────────────────────────────── */

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [vRes, cRes, eRes, fRes] = await Promise.allSettled([
        api.get('/courtoisie/vehicules'),
        api.get('/concessions'),
        api.get('/entites-juridiques'),
        api.get('/referentiels/flottes-courtoisie')
      ]);
      if (vRes.status === 'rejected') throw vRes.reason;
      if (cRes.status === 'rejected') throw cRes.reason;
      setVehicules(vRes.value.data.vehicules || []);
      setConcessions(cRes.value.data.concessions || cRes.value.data || []);
      // Flottes courtoisie (référentiel par concession) — non bloquant.
      setFlottes(fRes.status === 'fulfilled' ? (fRes.value.data.flottes || []) : []);
      // /entites-juridiques peut échouer (endpoint récent) : on n'empêche pas le chargement pour autant
      if (eRes.status === 'fulfilled') {
        setEntites(eRes.value.data.entites || []);
      } else {
        console.warn('[courtoisie] entites-juridiques indisponible:', eRes.reason?.response?.data || eRes.reason?.message);
        setEntites([]);
      }
    } catch (err) {
      console.error('Erreur chargement courtoisie:', err);
      setError('Impossible de charger les données.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Tous les prêts (internes + locations) pour l'onglet Prêts
  const loadPrets = useCallback(async () => {
    try {
      const res = await api.get('/courtoisie/prets');
      setPrets(res.data.prets || []);
    } catch (err) {
      console.warn('[courtoisie] prets indisponibles:', err.response?.data || err.message);
    }
  }, []);

  useEffect(() => { loadPrets(); }, [loadPrets]);

  /* ── Helpers ────────────────────────────────────────────────────────────── */

  const vehiculesActifs = vehicules; // déjà filtrés actif=true côté backend

  // ── Filtre Concession de la page (2026-09-07) ────────────────────────────
  // Réservé aux profils multi-concessions (sans concession_id) : les autres
  // sont cloisonnés côté serveur et n'ont pas de sélecteur. Il s'applique aux
  // 4 onglets. Un prêt/loueur sans concession (legacy) n'apparaît que sur
  // « Toutes les concessions ».
  const matchConcessionVehicule = (v) => filterConcession === 'all' || String(v.concession_id) === filterConcession;
  const matchConcessionPret = (p) => filterConcession === 'all' || String(p.concession_id || '') === filterConcession;
  const selectConcession = !user?.concession_id ? (
    <select className={`page-select${filterConcession !== 'all' ? ' active-filter' : ''}`} value={filterConcession} onChange={e => setFilterConcession(e.target.value)}>
      <option value="all">Toutes les concessions</option>
      {concessions.map(c => <option key={c.id} value={String(c.id)}>{c.nom}</option>)}
    </select>
  ) : null;

  /* ═══════════════════════════════════════════════════════════════════════════
     ONGLET 1 — FLOTTE
  ═══════════════════════════════════════════════════════════════════════════ */

  const flotteFiltree = vehiculesActifs.filter(v => {
    const okConcession = matchConcessionVehicule(v);
    const okStatut = filterStatutFlotte === 'all' || v.statut === filterStatutFlotte;
    const term = searchFlotte.toLowerCase();
    const okSearch = !term || (v.immatriculation || '').toLowerCase().includes(term) ||
      v.marque.toLowerCase().includes(term) || v.modele.toLowerCase().includes(term);
    return okConcession && okStatut && okSearch;
  });

  const openAjouterVehicule = () => {
    setEditVehicule(null);
    setFormVehicule(initVehicule);
    setShowModalVehicule(true);
  };

  const openEditerVehicule = (v) => {
    setEditVehicule(v);
    setFormVehicule({
      entite_juridique_id: v.entite_juridique_id || '',
      concession_id: v.concession_id || '',
      flotte_id: v.flotte_id || '',
      immatriculation: v.immatriculation || '',
      marque: v.marque,
      modele: v.modele,
      carburant: v.carburant,
      boite_vitesse: v.boite_vitesse,
      km_max: v.km_max ?? '',
      date_limite: v.date_limite ? String(v.date_limite).split('T')[0] : ''
    });
    setShowModalVehicule(true);
  };

  const handleSaveVehicule = async () => {
    setSavingVehicule(true);
    try {
      // Quand une entité juridique est choisie, le backend dérive la concession — on l'ignore côté front.
      const payload = {
        ...formVehicule,
        immatriculation: normaliserImmat(formVehicule.immatriculation),
        entite_juridique_id: formVehicule.entite_juridique_id ? parseInt(formVehicule.entite_juridique_id, 10) : null,
        flotte_id: formVehicule.flotte_id ? parseInt(formVehicule.flotte_id, 10) : null,
      };
      delete payload.concession_id;
      // Seuils réservés au super-admin : on ne les envoie pas sinon (le backend renverrait 403).
      if (user?.role !== 'super-admin') { delete payload.km_max; delete payload.date_limite; }
      else {
        payload.km_max = payload.km_max === '' ? null : payload.km_max;
        payload.date_limite = payload.date_limite || null;
      }
      if (editVehicule) {
        await api.put(`/courtoisie/vehicules/${editVehicule.id}`, payload);
      } else {
        await api.post('/courtoisie/vehicules', payload);
      }
      setShowModalVehicule(false);
      await loadData();
    } catch (err) {
      alert('Erreur lors de la sauvegarde : ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingVehicule(false);
    }
  };

  const handleDesactiverVehicule = async (v) => {
    if (!window.confirm(`Désactiver ${v.immatriculation} ${v.marque} ${v.modele} ?`)) return;
    try {
      await api.delete(`/courtoisie/vehicules/${v.id}`);
      await loadData();
    } catch (err) {
      alert('Erreur lors de la désactivation');
    }
  };

  const handleChangerStatutVehicule = async (v, statut) => {
    try {
      await api.put(`/courtoisie/vehicules/${v.id}`, { statut });
      await loadData();
    } catch (err) {
      alert('Erreur lors du changement de statut');
    }
  };

  const handleInlineUpdate = async (vehiculeId, field, value) => {
    try {
      await api.put(`/courtoisie/vehicules/${vehiculeId}`, { [field]: value });
      setVehicules(prev => prev.map(v => v.id === vehiculeId ? { ...v, [field]: value } : v));
    } catch (err) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const CARBURANT_MAP = {
    'essence': 'essence', 'diesel': 'diesel', 'electrique': 'electrique',
    'électrique': 'electrique', 'hybride': 'hybride',
  };
  const BOITE_MAP = {
    'bvm': 'BVM', 'manuelle': 'BVM', 'bva': 'BVA', 'automatique': 'BVA', 'auto': 'BVA',
    'BVM': 'BVM', 'BVA': 'BVA',
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];

      // Détecter la ligne d'en-têtes : chercher la ligne contenant "Immatriculation"
      const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const headerRowIdx = rawRows.findIndex(row =>
        row.some(cell => String(cell).toLowerCase().includes('immatriculation'))
      );
      if (headerRowIdx === -1) {
        alert('Impossible de trouver la ligne d\'en-têtes dans le fichier. Vérifiez que la colonne "Immatriculation" est présente.');
        setImporting(false);
        return;
      }
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '', range: headerRowIdx });

      const vehicules = rows.map(r => {
        const immat     = (r['Immatriculation'] || r['immatriculation'] || '').toString().trim();
        const marque    = (r['Marque'] || r['marque'] || '').toString().trim();
        const modele    = (r['Modèle'] || r['Modele'] || r['modele'] || '').toString().trim();
        const carburant = CARBURANT_MAP[(r['Carburant'] || r['carburant'] || '').toString().toLowerCase().trim()] || 'essence';
        const boiteRaw  = (r['Boîte'] || r['Boite'] || r['boite_vitesse'] || r['Boite de vitesse'] || '').toString().trim();
        const boite     = BOITE_MAP[boiteRaw] || BOITE_MAP[boiteRaw.toUpperCase()] || 'BVM';
        const concNom   = (r['Concession'] || r['concession'] || '').toString().trim();
        const concession = concNom ? concessions.find(c => c.nom.toLowerCase() === concNom.toLowerCase()) : null;
        return {
          immatriculation: immat,
          marque,
          modele,
          carburant,
          boite_vitesse: boite,
          concession_id: concession ? concession.id : null,
        };
      }).filter(v => v.immatriculation && v.marque && v.modele);

      if (vehicules.length === 0) {
        alert('Aucune ligne valide trouvée dans le fichier.');
        setImporting(false);
        return;
      }

      const res = await api.post('/courtoisie/vehicules/import', { vehicules });
      setImportResult(res.data);
      await loadData();
    } catch (err) {
      alert('Erreur lors de l\'import : ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
    }
  };

  const renderFlotte = () => (
    <>
      {/* Input file caché pour import Excel */}
      <input id="vc-import-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImportExcel} disabled={importing} />

      {/* Toolbar filtres */}
      <div className="page-toolbar">
        <input type="text" className="page-search" placeholder="🔍 Rechercher..." value={searchFlotte} onChange={e => setSearchFlotte(e.target.value)} />
        {selectConcession}
        <select className={`page-select${filterStatutFlotte !== 'all' ? ' active-filter' : ''}`} value={filterStatutFlotte} onChange={e => setFilterStatutFlotte(e.target.value)}>
          <option value="all">Tous les statuts</option>
          <option value="disponible">Disponible</option>
          <option value="en_pret">En prêt</option>
          <option value="maintenance">Maintenance</option>
          <option value="hors_service">Hors service</option>
        </select>
      </div>

      {importResult && (
        <div style={{ margin: '0 24px 12px', padding: '12px 16px', borderRadius: 8, background: importResult.errors.length === 0 ? 'light-dark(#dcfce7, var(--color-success-light))' : 'light-dark(#fef3c7, var(--color-warning-light))', border: `1px solid ${importResult.errors.length === 0 ? '#86efac' : '#fcd34d'}` }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            ✅ {importResult.imported} véhicule{importResult.imported !== 1 ? 's' : ''} importé{importResult.imported !== 1 ? 's' : ''}
            {importResult.errors.length > 0 && ` · ⚠️ ${importResult.errors.length} ligne${importResult.errors.length !== 1 ? 's' : ''} ignorée${importResult.errors.length !== 1 ? 's' : ''}`}
          </div>
          {importResult.errors.length > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--color-warning-dark)' }}>
              {importResult.errors.map((e, i) => (
                <li key={i}><strong>{e.immatriculation || '—'}</strong> : {e.raison}</li>
              ))}
            </ul>
          )}
          <button onClick={() => setImportResult(null)} style={{ marginTop: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)', textDecoration: 'underline' }}>Fermer</button>
        </div>
      )}

      <div className="table-container">
          <table className="vc-table">
            <thead>
              <tr>
                <th>Immatriculation</th>
                <th>Marque / Modèle</th>
                <th>Carburant</th>
                <th>Boîte</th>
                <th>Entité juridique</th>
                <th>Flotte</th>
                <th>Statut</th>
                {/* Planification du dossier CLIENT en cours : le vehicule de
                    courtoisie revient quand l'atelier rend la voiture du client. */}
                <th>Planification</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flotteFiltree.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>
                  Aucun véhicule — <button className="vc-history-toggle" onClick={openAjouterVehicule}>Ajouter le premier</button>
                </td></tr>
              )}
              {flotteFiltree.map(v => (
                <tr key={v.id} className="vc-row" style={{ cursor: 'pointer' }} onClick={() => handleDetailVehicule(v)}>
                  <td><span className="plaque-badge">{v.immatriculation || '—'}</span></td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{v.marque} {v.modele}</div>
                  </td>
                  <td>
                    <select
                      className="vc-inline-select"
                      value={v.carburant || 'essence'}
                      onChange={e => handleInlineUpdate(v.id, 'carburant', e.target.value)}
                      onClick={e => e.stopPropagation()}
                    >
                      <option value="essence">Essence</option>
                      <option value="diesel">Diesel</option>
                      <option value="electrique">Électrique</option>
                      <option value="hybride">Hybride</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className="vc-inline-select"
                      value={v.boite_vitesse || 'BVM'}
                      onChange={e => handleInlineUpdate(v.id, 'boite_vitesse', e.target.value)}
                      onClick={e => e.stopPropagation()}
                    >
                      <option value="BVM">BVM</option>
                      <option value="BVA">BVA</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {v.entite_juridique_nom ? (
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{v.entite_juridique_nom}</span>
                    ) : v.concession_nom ? (
                      <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }} title="Véhicule sans entité juridique — à éditer">
                        {v.concession_nom}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <select
                      className="vc-inline-select"
                      value={v.flotte_id || ''}
                      onChange={e => handleInlineUpdate(v.id, 'flotte_id', e.target.value || null)}
                      onClick={e => e.stopPropagation()}
                    >
                      <option value="">—</option>
                      {flottes.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                    </select>
                  </td>
                  <td>
                    <StatutBadge statut={v.statut} />
                    {v.bloque && (
                      <span className="vc-statut-badge" title={v.raison_blocage || ''}
                        style={{ color: 'var(--color-error-dark)', background: 'var(--color-error-light)', marginLeft: 6, cursor: 'help' }}>⛔ Bloqué</span>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <PlanificationCell dossier={v} onOuvrir={ouvrirDossier} />
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="vc-btn-action" style={{ background: '#64748b', color: 'var(--text-inverse)' }} onClick={() => openEditerVehicule(v)}>✏️ Modifier</button>
                      {v.statut === 'disponible' && (
                        <button className="vc-btn-action" style={{ background: '#f59e0b', color: 'var(--text-inverse)' }} onClick={() => handleChangerStatutVehicule(v, 'maintenance')}>🔧 Maintenance</button>
                      )}
                      {v.statut === 'maintenance' && (
                        <button className="vc-btn-action" style={{ background: '#10b981', color: 'var(--text-inverse)' }} onClick={() => handleChangerStatutVehicule(v, 'disponible')}>✅ Disponible</button>
                      )}
                      {v.statut !== 'en_pret' && (
                        <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }} onClick={() => handleDesactiverVehicule(v)}>🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     ONGLET 2 — PRÊTS
  ═══════════════════════════════════════════════════════════════════════════ */

  const handleNouveauPret = (vehicule = null, mode = 'interne') => {
    setPretMode(mode);
    setFormPret({ ...initPret, vehicule_id: vehicule ? vehicule.id : '' });
    if (mode === 'location') loadLoueurs();
    clearPretFichiersBuffer();
    setModalType('nouveau-pret');
    setShowModal(true);
  };

  const closeNouveauPret = () => { clearPretFichiersBuffer(); setShowModal(false); };

  const handleRetourVehicule = (vehicule) => {
    setSelectedVehicule(vehicule);
    setSelectedPretId(vehicule.pret_id);
    setFormRetour({ ...initRetour });
    setModalType('retour');
    setShowModal(true);
  };

  // Restitution depuis l'onglet Prêts (un prêt, pas un véhicule de flotte) : on
  // reconstruit l'objet attendu par la modale de retour (immat/marque + km départ).
  const handleRetourPret = (pret) => {
    setSelectedVehicule({
      immatriculation: pret.immatriculation || pret.loueur_immat || '—',
      marque: pret.marque || pret.loueur_marque || '',
      modele: pret.modele || '',
      pret_id: pret.id,
      client_nom: pret.client_nom,
      client_prenom: pret.client_prenom,
      date_debut: pret.date_debut,
      km_depart: pret.km_depart,
    });
    setSelectedPretId(pret.id);
    setFormRetour({ ...initRetour });
    setModalType('retour');
    setShowModal(true);
  };

  // Ouvre la modale « Documents » d'un prêt (fonctionne aussi pour une location,
  // qui n'a pas de véhicule de flotte). Clé = pret.id.
  const handleOpenDocuments = async (pret) => {
    const label = `${(pret.client_nom || '').trim()} ${(pret.client_prenom || '').trim()}`.trim();
    setFichiers([]);
    await loadFichiers(pret.id);
    setDocPret({ id: pret.id, label });
  };

  const handleDetailVehicule = (vehicule) => {
    setSelectedVehicule(vehicule);
    setHistorique([]);
    setShowHistory(false);
    setEditingPret(false);
    setFichiers([]);
    if (vehicule.pret_id) loadFichiers(vehicule.pret_id);
    setModalType('detail');
    setShowModal(true);
  };

  // Ouvre la même modale détail à partir d'un PRÊT (onglet Prêts) — interne OU location.
  // On construit un objet de la même forme que le véhicule + prêt en cours embarqué.
  const handleDetailPret = (p) => {
    setSelectedVehicule({
      id: p.vehicule_id,                 // pour l'historique (null si location)
      immatriculation: p.immatriculation,
      marque: p.marque, modele: p.modele,
      statut: null, concession_nom: p.concession_nom,
      carburant: null, boite_vitesse: null,
      pret_id: p.id, pret_statut: p.statut, type_pret: p.type_pret,
      client_nom: p.client_nom, client_prenom: p.client_prenom,
      client_telephone: p.client_telephone, client_email: p.client_email,
      date_debut: p.date_debut, date_fin_prevue: p.date_fin_prevue, date_retour: p.date_retour,
      km_depart: p.km_depart, km_retour: p.km_retour, motif: p.motif,
      immat_client: p.immat_client, or_numero: p.or_numero,
      caution_type: p.caution_type, caution_montant: p.caution_montant, caution_cheque_numero: p.caution_cheque_numero,
      contrat_signe_s3_key: p.contrat_signe_s3_key,
      cout_journalier: p.cout_journalier, facturation: p.facturation,
      societe: p.societe, autorise_par: p.autorise_par, num_bon_commande: p.num_bon_commande,
      loueur_nom: p.loueur_nom, loueur_immat: p.loueur_immat, loueur_marque: p.loueur_marque, loueur_num_contrat: p.loueur_num_contrat,
    });
    setHistorique([]); setShowHistory(false); setEditingPret(false);
    setFichiers([]); loadFichiers(p.id);
    setModalType('detail'); setShowModal(true);
  };

  const handleLoadHistorique = async (vehiculeId) => {
    try {
      const res = await api.get(`/courtoisie/vehicules/${vehiculeId}/historique`);
      setHistorique(res.data.prets || []);
      setShowHistory(true);
    } catch (err) {
      alert('Erreur lors du chargement de l\'historique');
    }
  };

  const handleSavePret = async () => {
    // Cohérence km côté UI (prêt interne) : départ ≥ dernier km connu du véhicule.
    if (pretMode === 'interne') {
      const veh = vehiculesActifs.find(v => String(v.id) === String(formPret.vehicule_id));
      if (veh && veh.dernier_km_connu != null && formPret.km_depart !== '' &&
          Number(formPret.km_depart) < Number(veh.dernier_km_connu)) {
        alert(`Le kilométrage de départ (${formPret.km_depart}) ne peut pas être inférieur au dernier kilométrage connu du véhicule (${veh.dernier_km_connu} km).`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...formPret,
        immat_client: normaliserImmat(formPret.immat_client),
        loueur_immat: normaliserImmat(formPret.loueur_immat),
        client_nom: (formPret.client_nom || '').toUpperCase()
      };
      const res = pretMode === 'location'
        ? await api.post('/courtoisie/prets/location', payload)
        : await api.post(`/courtoisie/vehicules/${formPret.vehicule_id}/pret`, payload);
      // Téléverser les pièces jointes saisies dans la modale, maintenant que le prêt
      // possède un id. On compte les échecs pour ne pas perdre le prêt si un upload casse.
      const nouveauPret = res?.data?.pret;
      let echecsFichiers = 0;
      if (nouveauPret?.id && pretFichiersBuffer.length) {
        for (const item of pretFichiersBuffer) {
          try {
            const fd = new FormData();
            fd.append('file', item.file);
            await api.post(`/courtoisie/prets/${nouveauPret.id}/fichiers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch { echecsFichiers++; }
        }
      }
      clearPretFichiersBuffer();
      setShowModal(false);
      await Promise.all([loadData(), loadPrets()]);
      if (echecsFichiers > 0) {
        alert(`Prêt créé. En revanche ${echecsFichiers} pièce(s) jointe(s) n'ont pas pu être envoyée(s) — vous pouvez les ré-ajouter depuis le détail du prêt.`);
      }
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRetour = async () => {
    // Cohérence km côté UI : retour ≥ départ.
    const kmDep = selectedVehicule?.km_depart;
    if (formRetour.km_retour !== '' && kmDep != null &&
        Number(formRetour.km_retour) < Number(kmDep)) {
      alert(`Le kilométrage de retour (${formRetour.km_retour}) ne peut pas être inférieur au kilométrage de départ (${kmDep} km).`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/courtoisie/prets/${selectedPretId}/retour`, formRetour);
      setShowModal(false);
      await Promise.all([loadData(), loadPrets()]);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadContrat = async (pretId) => {
    setGenPdf(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = (process.env.REACT_APP_API_URL || '') + '/api';
      const res = await fetch(`${apiBase}/courtoisie/prets/${pretId}/contrat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur serveur');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat-pret-N${String(pretId).padStart(5, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors de la génération du contrat PDF');
    } finally {
      setGenPdf(false);
    }
  };

  const handleUploadContratSigne = async (pretId, file) => {
    setUploadingContrat(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = (process.env.REACT_APP_API_URL || '') + '/api';
      const formData = new FormData();
      formData.append('contrat', file);
      const res = await fetch(`${apiBase}/courtoisie/prets/${pretId}/contrat-signe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Erreur upload');
      await loadData();
      setSelectedVehicule(prev => ({ ...prev, contrat_signe_s3_key: 'done' }));
    } catch (err) {
      alert("Erreur lors de l'upload du contrat signé");
    } finally {
      setUploadingContrat(false);
    }
  };

  const handleVoirContratSigne = async (pretId) => {
    try {
      const res = await api.get(`/courtoisie/prets/${pretId}/contrat-signe`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert('Erreur lors de l\'ouverture du contrat signé');
    }
  };

  const handleSupprimerContratSigne = async (pretId) => {
    if (!window.confirm('Supprimer le contrat signé ?')) return;
    try {
      await api.delete(`/courtoisie/prets/${pretId}/contrat-signe`);
      await loadData();
      setSelectedVehicule(prev => ({ ...prev, contrat_signe_s3_key: null }));
    } catch (err) {
      alert('Erreur lors de la suppression du contrat signé');
    }
  };

  const handleSaveEditPret = async (pretId) => {
    setSaving(true);
    try {
      const payload = {
        ...formEditPret,
        immat_client: normaliserImmat(formEditPret.immat_client),
      };
      await api.patch(`/courtoisie/prets/${pretId}`, payload);
      await loadData();
      setSelectedVehicule(prev => ({ ...prev, ...payload,
        immat_client: normaliserImmat(formEditPret.immat_client)
      }));
      setEditingPret(false);
    } catch (err) {
      alert('Erreur lors de la sauvegarde : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  /* ── Partenaires loueurs ────────────────────────────────────────────────── */

  const loadLoueurs = useCallback(async () => {
    try {
      const params = user?.concession_id ? { concession_id: user.concession_id } : {};
      const res = await api.get('/courtoisie/loueurs', { params });
      setLoueurs(res.data.loueurs || []);
    } catch (err) {
      console.warn('[courtoisie] loueurs indisponibles:', err.response?.data || err.message);
    }
  }, [user]);

  useEffect(() => { loadLoueurs(); }, [loadLoueurs]);

  const openAjouterLoueur = () => {
    setEditLoueur(null);
    setFormLoueur({ ...initLoueur, concession_id: user?.concession_id ? String(user.concession_id) : '' });
    setShowModalLoueur(true);
  };
  const openEditerLoueur = (l) => {
    setEditLoueur(l);
    setFormLoueur({ nom: l.nom || '', concession_id: l.concession_id ? String(l.concession_id) : '' });
    setShowModalLoueur(true);
  };
  const handleSaveLoueur = async () => {
    if (!formLoueur.nom.trim()) return;
    setSavingLoueur(true);
    try {
      const payload = { nom: formLoueur.nom.trim(), concession_id: formLoueur.concession_id ? parseInt(formLoueur.concession_id, 10) : null };
      if (editLoueur) await api.put(`/courtoisie/loueurs/${editLoueur.id}`, payload);
      else await api.post('/courtoisie/loueurs', payload);
      setShowModalLoueur(false);
      await loadLoueurs();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingLoueur(false);
    }
  };
  const handleDeleteLoueur = async (l) => {
    if (!window.confirm(`Supprimer le loueur « ${l.nom} » ?`)) return;
    try { await api.delete(`/courtoisie/loueurs/${l.id}`); await loadLoueurs(); }
    catch { alert('Erreur lors de la suppression'); }
  };

  /* ── Pièces jointes du prêt ─────────────────────────────────────────────── */

  const loadFichiers = async (pretId) => {
    try {
      const res = await api.get(`/courtoisie/prets/${pretId}/fichiers`);
      setFichiers(res.data.fichiers || []);
    } catch (err) { setFichiers([]); }
  };
  const handleUploadFichier = async (pretId, file) => {
    setUploadingFichier(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/courtoisie/prets/${pretId}/fichiers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadFichiers(pretId);
    } catch (err) {
      alert("Erreur lors de l'upload : " + (err.response?.data?.error || err.message));
    } finally { setUploadingFichier(false); }
  };
  const handleDeleteFichier = async (pretId, fichierId) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try { await api.delete(`/courtoisie/prets/${pretId}/fichiers/${fichierId}`); await loadFichiers(pretId); }
    catch { alert('Erreur lors de la suppression'); }
  };

  /* ── Planning hebdo ─────────────────────────────────────────────────────── */

  const getWeekDates = () => {
    const today = new Date();
    const diff = today.getDay() === 0 ? -6 : 1 - today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff + currentWeek * 7);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  };

  const isPretActiveOnDate = (v, date) => {
    if (!v.pret_id || !v.date_debut || !v.date_fin_prevue) return false;
    const ds = date.toISOString().split('T')[0];
    return ds >= v.date_debut && ds <= v.date_fin_prevue;
  };

  // Prédicat de recherche partagé (board véhicules + archives).
  const matchSearchPret = (p, term) => !term ||
    (p.immatriculation || '').toLowerCase().includes(term) ||
    (p.loueur_immat || '').toLowerCase().includes(term) ||
    (p.marque || '').toLowerCase().includes(term) ||
    (p.loueur_marque || '').toLowerCase().includes(term) ||
    (p.loueur_nom || '').toLowerCase().includes(term) ||
    (p.client_nom || '').toLowerCase().includes(term) ||
    (p.client_prenom || '').toLowerCase().includes(term);

  // ── Onglet « Prêts & Statuts » : board PAR VÉHICULE ──────────────────────────
  // Une ligne = l'état courant d'un véhicule : prêté (client + période + Restituer)
  // ou « ✅ Disponible » (+ Nouveau prêt), maintenance / hors service / bloqué.
  // + les locations partenaires EN COURS (qui n'ont pas de véhicule de flotte).
  // Les contrats restitués ne sont PLUS ici : ils basculent dans l'onglet Archives.
  const renderBoardVehicules = () => {
    const term = searchPret.toLowerCase();
    const internes = vehiculesActifs.filter(matchConcessionVehicule).filter(v => !term ||
      (v.immatriculation || '').toLowerCase().includes(term) ||
      (v.marque || '').toLowerCase().includes(term) ||
      (v.modele || '').toLowerCase().includes(term) ||
      (v.client_nom || '').toLowerCase().includes(term) ||
      (v.client_prenom || '').toLowerCase().includes(term));
    const locations = prets.filter(p => p.type_pret === 'location' && p.statut === 'en_cours' && matchConcessionPret(p) && matchSearchPret(p, term));
    const empty = internes.length === 0 && locations.length === 0;

    return (
      <div className="table-container">
        <table className="vc-table">
          <thead>
            <tr>
              <th>Immatriculation</th>
              <th>Marque / Modèle</th>
              <th>Client / Période</th>
              {/* Dossier atelier du CLIENT : c'est lui qui dit quand le
                  vehicule de courtoisie doit revenir. */}
              <th>Planification</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {empty && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Aucun véhicule</td></tr>
            )}

            {internes.map(v => {
              const onLoan = !!v.pret_id;
              const dispo = v.statut === 'disponible' && !v.bloque;
              return (
                <tr key={`v${v.id}`} className="vc-row" style={{ cursor: 'pointer' }} onClick={() => handleDetailVehicule(v)}>
                  <td><span className="plaque-badge">{v.immatriculation || '—'}</span></td>
                  <td><div style={{ fontWeight: 600 }}>{v.marque} {v.modele}</div></td>
                  <td>
                    {onLoan ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{v.client_nom} {v.client_prenom}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {formatDate(v.date_debut)} → <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontWeight: 600 }}>{formatDate(v.date_fin_prevue)}</span>
                        </div>
                      </>
                    ) : v.bloque ? (
                      <span className="vc-statut-badge" title={v.raison_blocage || ''} style={{ color: 'var(--color-error-dark)', background: 'var(--color-error-light)', cursor: 'help' }}>⛔ Bloqué</span>
                    ) : dispo ? (
                      <span className="vc-statut-badge" style={{ color: STATUT_COLOR.disponible, background: STATUT_BG.disponible }}>✅ Disponible</span>
                    ) : v.statut === 'maintenance' ? (
                      <span className="vc-statut-badge" style={{ color: STATUT_COLOR.maintenance, background: STATUT_BG.maintenance }}>🔧 Maintenance</span>
                    ) : (
                      <span className="vc-statut-badge" style={{ color: STATUT_COLOR.hors_service, background: STATUT_BG.hors_service }}>⛔ Hors service</span>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <PlanificationCell dossier={v} onOuvrir={ouvrirDossier} />
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {onLoan ? (
                        <button className="vc-btn-action vc-btn-retour" title="Restituer le véhicule"
                          onClick={() => handleRetourVehicule(v)}>📥 Restituer</button>
                      ) : dispo ? (
                        <button className="vc-btn-action vc-btn-pret" title="Ouvrir un nouveau prêt sur ce véhicule"
                          onClick={() => handleNouveauPret(v)}>📝 Nouveau prêt</button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {locations.map(p => (
              <tr key={`p${p.id}`} className="vc-row" style={{ cursor: 'pointer' }} onClick={() => handleDetailPret(p)}>
                <td><span style={{ marginRight: 4 }}>🤝</span><span className="plaque-badge">{p.loueur_immat || '—'}</span></td>
                <td>
                  <div style={{ fontWeight: 600 }}>{p.loueur_marque || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    loué chez {p.loueur_nom || '—'}{p.loueur_num_contrat ? ` · contrat ${p.loueur_num_contrat}` : ''}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>{p.client_nom} {p.client_prenom}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDate(p.date_debut)} → <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontWeight: 600 }}>{formatDate(p.date_fin_prevue)}</span>
                  </div>
                </td>
                <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  <PlanificationCell dossier={p} onOuvrir={ouvrirDossier} />
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="vc-btn-action vc-btn-retour" title="Restituer le véhicule"
                    onClick={() => handleRetourPret(p)}>📥 Restituer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Onglet « Archives » : contrats restitués (terminés) ou annulés ───────────
  const archivesListe = prets
    .filter(p => p.statut === 'termine' || p.statut === 'annule')
    .filter(matchConcessionPret)
    .filter(p => filterStatutPret === 'all' || p.statut === filterStatutPret)
    .filter(p => matchSearchPret(p, searchPret.toLowerCase()));

  const renderArchives = () => (
    <>
      {/* Toolbar : recherche + filtre statut (terminés / annulés) */}
      <div className="page-toolbar">
        <input type="text" className="page-search" placeholder="🔍 Rechercher..." value={searchPret} onChange={e => setSearchPret(e.target.value)} />
        {selectConcession}
        <select className={`page-select${filterStatutPret !== 'all' ? ' active-filter' : ''}`} value={filterStatutPret} onChange={e => setFilterStatutPret(e.target.value)}>
          <option value="all">Tous les archivés</option>
          <option value="termine">Terminés</option>
          <option value="annule">Annulés</option>
        </select>
      </div>

      <div className="table-container">
        <table className="vc-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Véhicule</th>
              <th>Client</th>
              <th>Période</th>
              <th>Km</th>
              <th style={{ textAlign: 'right' }}>Facturation</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {archivesListe.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Aucun contrat archivé</td></tr>
            )}
            {archivesListe.map(p => {
              const st = STATUT_PRET[p.statut] || { label: p.statut, color: 'light-dark(#64748b, var(--text-secondary))', bg: 'light-dark(#f1f5f9, var(--bg-muted))' };
              const isLoc = p.type_pret === 'location';
              return (
                <tr key={p.id} className="vc-row" style={{ cursor: 'pointer' }} onClick={() => handleDetailPret(p)}>
                  <td>
                    <span className="vc-statut-badge" style={{ color: isLoc ? 'light-dark(#6d28d9, var(--color-decision))' : 'light-dark(#1d4ed8, var(--color-info-dark))', background: isLoc ? 'light-dark(#ede9fe, var(--color-decision-light))' : 'light-dark(#dbeafe, var(--color-info-light))' }}>
                      {isLoc ? '🤝 Location' : '🏢 Interne'}
                    </span>
                  </td>
                  <td>
                    {isLoc ? (
                      <>
                        <div style={{ fontWeight: 600 }}><span className="plaque-badge">{p.loueur_immat || '—'}</span> {p.loueur_marque || ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          loué chez {p.loueur_nom || '—'}{p.loueur_num_contrat ? ` · contrat ${p.loueur_num_contrat}` : ''}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 600 }}><span className="plaque-badge">{p.immatriculation || '—'}</span></div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.marque} {p.modele}</div>
                      </>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.client_nom} {p.client_prenom}</div>
                    {p.client_telephone && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{p.client_telephone}</div>}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatDate(p.date_debut)} → {p.date_retour
                      ? formatDate(p.date_retour)
                      : <span style={{ color: 'var(--text-muted)' }}>{formatDate(p.date_fin_prevue)}</span>}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {p.km_depart != null || p.km_retour != null
                      ? <>{p.km_depart ?? '—'} → {p.km_retour ?? '—'}</>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {p.facturation
                      ? <strong title={`${p.facturation.jours} j × ${p.facturation.cout_journalier} € HT · TVA ${p.facturation.taux_tva} %`}>
                          {p.facturation.ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € TTC
                        </strong>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td><span className="vc-statut-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', padding: '4px 8px' }}
                        title="Pièces jointes (permis, CNI…)" aria-label="Documents" onClick={() => handleOpenDocuments(p)}>📎</button>
                      <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', padding: '4px 8px' }}
                        title="Contrat PDF" aria-label="Contrat" onClick={() => handleDownloadContrat(p.id)} disabled={genPdf}>📄</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderPlanningHebdo = () => {
    const weekDates = getWeekDates();
    return (
      <div className="planning-container">
        <div className="planning-navigation">
          <button onClick={() => setCurrentWeek(w => w - 1)}>← Semaine préc.</button>
          <button onClick={() => setCurrentWeek(0)}>Aujourd'hui</button>
          <button onClick={() => setCurrentWeek(w => w + 1)}>Semaine suiv. →</button>
        </div>
        <div className="planning-grid">
          <div className="planning-header">
            <div className="planning-cell header-cell">Véhicule</div>
            {weekDates.map((date, i) => (
              <div key={i} className="planning-cell header-cell">
                <div>{['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][date.getDay()]}</div>
                <div className="date-small">{date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</div>
              </div>
            ))}
          </div>
          {vehiculesActifs.filter(matchConcessionVehicule).map(v => (
            <div key={v.id} className="planning-row">
              <div className="planning-cell vehicule-cell">
                <span style={{ color: texteTheme(STATUT_COLOR[v.statut] || '#64748b') }}>●</span> {v.immatriculation || '—'}
                <div className="vehicule-small">{v.marque} {v.modele}</div>
              </div>
              {weekDates.map((date, i) => {
                const isActive = isPretActiveOnDate(v, date);
                return (
                  <div key={i} className={`planning-cell day-cell ${isActive ? 'occupied' : 'free'}`}>
                    {isActive && i === 0 && <div className="pret-bar">{v.client_nom}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPrets = () => (
    <>
      {/* Toolbar : recherche + bascule Tableau / Semaine (l'état se lit par véhicule) */}
      <div className="page-toolbar">
        <input type="text" className="page-search" placeholder="🔍 Rechercher..." value={searchPret} onChange={e => setSearchPret(e.target.value)} />
        {selectConcession}
        <select className={`page-select${viewMode !== 'tableau' ? ' active-filter' : ''}`} value={viewMode} onChange={e => setViewMode(e.target.value)}>
          <option value="tableau">📋 Tableau</option>
          <option value="planning-hebdo">📅 Semaine</option>
        </select>
      </div>
      {viewMode === 'tableau' && renderBoardVehicules()}
      {viewMode === 'planning-hebdo' && renderPlanningHebdo()}
    </>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     MODAUX
  ═══════════════════════════════════════════════════════════════════════════ */

  // ── Modal ajout/édition véhicule ─────────────────────────────────────────

  const renderModalVehicule = () => {
    if (!showModalVehicule) return null;
    const titre = editVehicule ? `✏️ Modifier ${editVehicule.immatriculation || ''}` : '🚗 Ajouter un véhicule';
    const canSave = formVehicule.immatriculation && formVehicule.marque && formVehicule.modele;
    return (
      <div className="modal-overlay">
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{titre}</h2>
            <button className="modal-close" onClick={() => setShowModalVehicule(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="form-section">
              <h3>Identification</h3>
              <div className="form-row">
                <div>
                  <label>Immatriculation *</label>
                  <input type="text" placeholder="AB123CD" value={formVehicule.immatriculation}
                    onChange={e => setFormVehicule(f => ({ ...f, immatriculation: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 }} />
                </div>
                <div>
                  <label>Entité juridique {!editVehicule && '*'}</label>
                  <select
                    value={formVehicule.entite_juridique_id}
                    onChange={e => setFormVehicule(f => ({ ...f, entite_juridique_id: e.target.value }))}
                  >
                    <option value="">— {editVehicule && !formVehicule.entite_juridique_id ? 'Sans entité (legacy)' : 'Choisir une entité'} —</option>
                    {entites.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.nom}{e.concession_nom && concessions.length > 1 ? ` — ${e.concession_nom}` : ''}
                      </option>
                    ))}
                  </select>
                  {entites.length === 0 && (
                    <div style={{ fontSize: 11, color: 'light-dark(var(--color-warning), var(--color-warning-dark))', marginTop: 4 }}>
                      Aucune entité juridique disponible. Créez-en une dans Administration / Entités juridiques.
                    </div>
                  )}
                </div>
                <div>
                  <label>Flotte</label>
                  <select
                    value={formVehicule.flotte_id}
                    onChange={e => setFormVehicule(f => ({ ...f, flotte_id: e.target.value }))}
                  >
                    <option value="">— Aucune flotte —</option>
                    {flottes.map(fl => (
                      <option key={fl.id} value={fl.id}>
                        {fl.nom}{fl.concession_nom && concessions.length > 1 ? ` — ${fl.concession_nom}` : ''}
                      </option>
                    ))}
                  </select>
                  {flottes.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Aucune flotte définie. Créez-en dans Administration / Référentiels / Flottes courtoisie.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Véhicule</h3>
              <div className="form-row">
                <div>
                  <label>Marque *</label>
                  <input type="text" placeholder="Renault" value={formVehicule.marque}
                    onChange={e => setFormVehicule(f => ({ ...f, marque: e.target.value }))} />
                </div>
                <div>
                  <label>Modèle *</label>
                  <input type="text" placeholder="Clio 5" value={formVehicule.modele}
                    onChange={e => setFormVehicule(f => ({ ...f, modele: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label>Carburant *</label>
                  <select value={formVehicule.carburant} onChange={e => setFormVehicule(f => ({ ...f, carburant: e.target.value }))}>
                    <option value="essence">Essence</option>
                    <option value="diesel">Diesel</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                  </select>
                </div>
                <div>
                  <label>Boîte de vitesse *</label>
                  <select value={formVehicule.boite_vitesse} onChange={e => setFormVehicule(f => ({ ...f, boite_vitesse: e.target.value }))}>
                    <option value="BVM">BVM — Manuelle</option>
                    <option value="BVA">BVA — Automatique</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Seuils de fin d'exploitation {user?.role !== 'super-admin' && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(modifiable par un super-admin)</span>}</h3>
              <div className="form-row">
                <div>
                  <label>Kilométrage maximum</label>
                  <input type="number" min="0" placeholder="ex : 120000" value={formVehicule.km_max}
                    disabled={user?.role !== 'super-admin'}
                    onChange={e => setFormVehicule(f => ({ ...f, km_max: e.target.value }))} />
                </div>
                <div>
                  <label>Date limite</label>
                  <input type="date" value={formVehicule.date_limite}
                    disabled={user?.role !== 'super-admin'}
                    onChange={e => setFormVehicule(f => ({ ...f, date_limite: e.target.value }))} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Au-delà d'un de ces seuils (km atteint ou date dépassée), le véhicule ne peut plus être prêté.
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowModalVehicule(false)}>Annuler</button>
            <button className="btn-primary" onClick={handleSaveVehicule} disabled={!canSave || savingVehicule}>
              {savingVehicule ? 'Enregistrement…' : editVehicule ? '💾 Enregistrer' : '➕ Ajouter'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Modal détail prêt ────────────────────────────────────────────────────

  const renderModalDetail = () => {
    if (!showModal || modalType !== 'detail' || !selectedVehicule) return null;
    const v = selectedVehicule;
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{v.type_pret === 'location'
              ? <>🤝 {v.loueur_immat || '—'} {v.loueur_marque ? `— ${v.loueur_marque}` : ''} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}>· loué chez {v.loueur_nom || '—'}</span></>
              : <>🚗 {v.immatriculation || '—'} — {v.marque} {v.modele}</>}</h2>
            <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="vc-detail-grid">
              {v.type_pret === 'location' ? (
                <>
                  <div className="vc-detail-field"><span>Type de prêt</span><span>🤝 Location partenaire</span></div>
                  <div className="vc-detail-field"><span>Loueur</span><span style={{ fontWeight: 600 }}>{v.loueur_nom || '—'}</span></div>
                  <div className="vc-detail-field"><span>Véhicule loué</span><span className="plaque-badge" style={{ fontSize: 12 }}>{v.loueur_immat || '—'}</span></div>
                  {v.loueur_num_contrat && <div className="vc-detail-field"><span>N° contrat loueur</span><span style={{ fontFamily: 'monospace' }}>{v.loueur_num_contrat}</span></div>}
                </>
              ) : (
                <>
                  {v.type_pret === 'interne'
                    ? <div className="vc-detail-field"><span>Type de prêt</span><span>🏢 Prêt interne</span></div>
                    : <div className="vc-detail-field"><span>Statut</span><StatutBadge statut={v.statut} /></div>}
                  <div className="vc-detail-field"><span>Concession</span><span>{v.concession_nom || '—'}</span></div>
                  {v.carburant && <div className="vc-detail-field"><span>Carburant</span><span>{CARBURANT_LABEL[v.carburant] || v.carburant}</span></div>}
                  {v.boite_vitesse && <div className="vc-detail-field"><span>Boîte</span><span>{v.boite_vitesse}</span></div>}
                </>
              )}
            </div>

            {v.pret_id && (
              <div className="vc-pret-actif">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="vc-section-title">📋 {v.pret_statut === 'termine' ? 'Prêt (restitué)' : v.pret_statut === 'annule' ? 'Prêt (annulé)' : 'Prêt en cours'}</div>
                  {(!v.pret_statut || v.pret_statut === 'en_cours') && (!editingPret
                    ? <button className="vc-btn-action" style={{ background: '#64748b', color: 'var(--text-inverse)', fontSize: 11 }}
                        onClick={() => { setFormEditPret({
                          date_fin_prevue: v.date_fin_prevue ? v.date_fin_prevue.split('T')[0] : '',
                          or_numero: v.or_numero || '',
                          immat_client: v.immat_client || '',
                          motif: v.motif || '',
                          client_telephone: v.client_telephone || '',
                          client_email: v.client_email || '',
                          km_depart: v.km_depart || '',
                          caution_type: v.caution_type || 'cheque',
                          caution_montant: v.caution_montant != null ? String(v.caution_montant) : '',
                          caution_cheque_numero: v.caution_cheque_numero || '',
                        }); setEditingPret(true); }}>
                        ✏️ Modifier
                      </button>
                    : <div style={{ display: 'flex', gap: 6 }}>
                        <button className="vc-btn-action" style={{ background: '#10b981', color: 'var(--text-inverse)', fontSize: 11 }}
                          onClick={() => handleSaveEditPret(v.pret_id)} disabled={saving}>
                          {saving ? '…' : '💾 Enregistrer'}
                        </button>
                        <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', fontSize: 11 }}
                          onClick={() => setEditingPret(false)}>
                          Annuler
                        </button>
                      </div>
                  )}
                </div>
                {!editingPret ? (
                  <div className="vc-detail-grid">
                    <div className="vc-detail-field"><span>Client</span><span style={{ fontWeight: 700 }}>{v.client_nom} {v.client_prenom}</span></div>
                    <div className="vc-detail-field"><span>Téléphone</span><span><a href={`tel:${v.client_telephone}`} style={{ color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}>{v.client_telephone || '—'}</a></span></div>
                    <div className="vc-detail-field"><span>Début</span><span>{formatDate(v.date_debut)}</span></div>
                    <div className="vc-detail-field"><span>Retour prévu</span><span style={{ fontWeight: 700, color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>{formatDate(v.date_fin_prevue)}</span></div>
                    <div className="vc-detail-field"><span>KM départ</span><span>{Number(v.km_depart || 0).toLocaleString('fr-FR')} km</span></div>
                    <div className="vc-detail-field"><span>Motif</span><span>{v.motif || '—'}</span></div>
                    {v.immat_client && <div className="vc-detail-field"><span>Véhicule client</span><span className="plaque-badge" style={{ fontSize: 12 }}>{v.immat_client}</span></div>}
                    {v.or_numero && <div className="vc-detail-field"><span>N° OR client</span><span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{v.or_numero}</span></div>}
                    {v.caution_type && (
                      <div className="vc-detail-field">
                        <span>Caution</span>
                        <span>
                          {CAUTION_LABEL[v.caution_type]}
                          {formatMontant(v.caution_montant) ? ` · ${formatMontant(v.caution_montant)}` : ''}
                          {v.caution_type === 'cheque' && v.caution_cheque_numero ? ` · n° ${v.caution_cheque_numero}` : ''}
                        </span>
                      </div>
                    )}
                    {v.km_retour != null && <div className="vc-detail-field"><span>KM retour</span><span>{Number(v.km_retour).toLocaleString('fr-FR')} km</span></div>}
                    {v.date_retour && <div className="vc-detail-field"><span>Date de restitution</span><span>{formatDate(v.date_retour)}</span></div>}
                    {v.societe && <div className="vc-detail-field"><span>Société (facturation)</span><span>{v.societe}</span></div>}
                    {v.autorise_par && <div className="vc-detail-field"><span>Autorisé par</span><span>{v.autorise_par}</span></div>}
                    {v.num_bon_commande && <div className="vc-detail-field"><span>N° bon de commande</span><span style={{ fontFamily: 'monospace' }}>{v.num_bon_commande}</span></div>}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginTop: 8 }}>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Date retour prévue</label>
                      <input type="date" value={formEditPret.date_fin_prevue}
                        onChange={e => setFormEditPret(f => ({ ...f, date_fin_prevue: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} /></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>N° OR client</label>
                      <input type="text" placeholder="ex : 12345" value={formEditPret.or_numero}
                        onChange={e => setFormEditPret(f => ({ ...f, or_numero: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} /></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Immat véhicule client</label>
                      <input type="text" placeholder="AB123CD" value={formEditPret.immat_client}
                        onChange={e => setFormEditPret(f => ({ ...f, immat_client: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }} /></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Motif</label>
                      <select value={formEditPret.motif} onChange={e => setFormEditPret(f => ({ ...f, motif: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }}>
                        {['Révision', 'Réparation', 'Carrosserie', 'Expertise', 'Autre'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Téléphone</label>
                      <input type="tel" value={formEditPret.client_telephone}
                        onChange={e => setFormEditPret(f => ({ ...f, client_telephone: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} /></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>KM départ</label>
                      <input type="number" value={formEditPret.km_depart}
                        onChange={e => setFormEditPret(f => ({ ...f, km_depart: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} /></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Caution — Type</label>
                      <select value={formEditPret.caution_type || 'cheque'}
                        onChange={e => setFormEditPret(f => ({
                          ...f,
                          caution_type: e.target.value,
                          caution_cheque_numero: e.target.value === 'cheque' ? f.caution_cheque_numero : '',
                        }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }}>
                        <option value="cheque">Chèque</option>
                        <option value="cb">Empreinte CB</option>
                      </select></div>
                    <div><label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Caution — Montant (€)</label>
                      <input type="number" min="0" step="0.01" value={formEditPret.caution_montant || ''}
                        onChange={e => setFormEditPret(f => ({ ...f, caution_montant: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} /></div>
                    {(formEditPret.caution_type || 'cheque') === 'cheque' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Caution — N° de chèque</label>
                        <input type="text" value={formEditPret.caution_cheque_numero || ''}
                          onChange={e => setFormEditPret(f => ({ ...f, caution_cheque_numero: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} />
                      </div>
                    )}
                  </div>
                )}
                {v.facturation && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-muted)', border: '1px solid var(--border-default)', borderRadius: 8 }}>
                    <div className="vc-section-title" style={{ fontSize: 13, marginBottom: 6 }}>💰 Facturation (refacturation interne — Datacar)</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '4px 14px', alignItems: 'baseline' }}>
                      <span>{v.facturation.jours} jour{v.facturation.jours > 1 ? 's' : ''} × {Number(v.cout_journalier || 0).toLocaleString('fr-FR')} €</span>
                      <span>·</span>
                      <span>HT <strong style={{ color: 'var(--text-primary)' }}>{v.facturation.ht.toLocaleString('fr-FR')} €</strong></span>
                      <span>·</span>
                      <span>TVA {v.facturation.taux_tva} % : {v.facturation.tva.toLocaleString('fr-FR')} €</span>
                      <span>·</span>
                      <span style={{ fontSize: 15 }}>TTC <strong style={{ color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}>{v.facturation.ttc.toLocaleString('fr-FR')} €</strong></span>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button className="vc-history-toggle" onClick={() => handleDownloadContrat(v.pret_id)} disabled={genPdf}>
                    {genPdf ? '⏳ Génération…' : '📄 Télécharger le contrat PDF'}
                  </button>
                  {v.contrat_signe_s3_key ? (
                    <>
                      <button className="vc-history-toggle" style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', borderColor: '#10b981' }}
                        onClick={() => handleVoirContratSigne(v.pret_id)}>
                        ✅ Voir contrat signé
                      </button>
                      <button className="vc-history-toggle" style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', borderColor: '#ef4444' }}
                        onClick={() => handleSupprimerContratSigne(v.pret_id)}>
                        🗑️ Supprimer
                      </button>
                    </>
                  ) : (
                    <label className="vc-history-toggle" style={{ cursor: 'pointer' }}>
                      {uploadingContrat ? '⏳ Upload…' : '📎 Uploader contrat signé'}
                      <input type="file" accept=".pdf" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) handleUploadContratSigne(v.pret_id, e.target.files[0]); }} />
                    </label>
                  )}
                </div>

                {/* Pièces jointes (permis, CNI…) */}
                <div style={{ marginTop: 14 }}>
                  <div className="vc-section-title" style={{ fontSize: 13 }}>📎 Pièces jointes (permis, CNI…)</div>
                  <CourtoisieDocsGrid
                    fichiers={fichiers}
                    pretId={v.pret_id}
                    onUpload={handleUploadFichier}
                    onDelete={handleDeleteFichier}
                    uploading={uploadingFichier}
                  />
                </div>
              </div>
            )}

            {v.id && <div className="vc-history-section">
              {!showHistory
                ? <button className="vc-history-toggle" onClick={() => handleLoadHistorique(v.id)}>▼ Voir l'historique des prêts</button>
                : <>
                    <button className="vc-history-toggle" onClick={() => setShowHistory(false)}>▲ Masquer l'historique</button>
                    {historique.length === 0
                      ? <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>Aucun historique</div>
                      : <div className="vc-history-scroll"><table className="vc-history-table">
                          <thead><tr><th>Client</th><th>Début</th><th>Retour</th><th style={{ textAlign: 'right' }}>KM</th><th>Documents</th></tr></thead>
                          <tbody>
                            {historique.map((h, i) => (
                              <tr key={i}>
                                <td>{h.client_nom} {h.client_prenom}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(h.date_debut)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{h.date_retour ? formatDate(h.date_retour) : <em style={{ color: 'var(--text-muted)' }}>en cours</em>}</td>
                                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                                  {h.km_retour && h.km_depart ? `+${(h.km_retour - h.km_depart).toLocaleString('fr-FR')}` : '—'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', fontSize: 11 }}
                                      title="Télécharger le contrat" onClick={() => handleDownloadContrat(h.id)} disabled={genPdf}>📄</button>
                                    {(h.fichiers || []).map(f => (
                                      <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title={f.nom} style={{ fontSize: 14, textDecoration: 'none' }}>📎</a>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table></div>
                    }
                  </>
              }
            </div>}
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowModal(false)}>Fermer</button>
            {v.statut === 'disponible' && (
              <button className="btn-primary" onClick={() => { setShowModal(false); handleNouveauPret(v); }}>📝 Nouveau prêt</button>
            )}
            {v.statut === 'en_pret' && (
              <button className="btn-primary" style={{ background: '#10b981' }} onClick={() => { setShowModal(false); handleRetourVehicule(v); }}>📥 Retour véhicule</button>
            )}
            {v.pret_statut === 'en_cours' && (
              <button className="btn-primary" style={{ background: '#10b981' }} onClick={() => { setShowModal(false); handleRetourPret({ id: v.pret_id, immatriculation: v.immatriculation, marque: v.marque, modele: v.modele, loueur_immat: v.loueur_immat, loueur_marque: v.loueur_marque, client_nom: v.client_nom, client_prenom: v.client_prenom, date_debut: v.date_debut, km_depart: v.km_depart }); }}>📥 Restituer le véhicule</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Modal nouveau prêt ───────────────────────────────────────────────────

  const renderModalNouveauPret = () => {
    if (!showModal || modalType !== 'nouveau-pret') return null;
    const isLocation = pretMode === 'location';
    const canSave = formPret.client_nom && formPret.date_debut && formPret.date_fin_prevue &&
      (isLocation ? (formPret.loueur_id || formPret.loueur_immat) : (formPret.vehicule_id && formPret.km_depart !== ''));
    return (
      <div className="modal-overlay">
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{isLocation ? '🤝 Nouveau prêt — Location partenaire' : '📝 Nouveau prêt interne'}</h2>
            <button className="modal-close" onClick={closeNouveauPret}>✕</button>
          </div>
          <div className="modal-body">
            <PretCourtoisieForm
              pretMode={pretMode}
              formPret={formPret}
              setFormPret={setFormPret}
              saving={saving}
              vehiculesActifs={vehiculesActifs}
              loueurs={loueurs}
              onOpenAjouterLoueur={openAjouterLoueur}
              pretFichiersBuffer={pretFichiersBuffer}
              addPretFichierBuffer={addPretFichierBuffer}
              removePretFichierBuffer={removePretFichierBuffer}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={closeNouveauPret}>Annuler</button>
            <button className="btn-primary" onClick={handleSavePret} disabled={!canSave || saving}>
              {saving ? 'Enregistrement…' : '📄 Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Modal retour véhicule ────────────────────────────────────────────────

  const renderModalRetour = () => {
    if (!showModal || modalType !== 'retour' || !selectedVehicule) return null;
    const v = selectedVehicule;
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📥 Retour Véhicule</h2>
            <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ background: 'var(--bg-muted)', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{v.immatriculation || '—'} — {v.marque} {v.modele}</div>
              {v.pret_id && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Prêté à {v.client_nom} {v.client_prenom} depuis le {formatDate(v.date_debut)}</div>}
            </div>
            <div className="form-section">
              <div className="form-row">
                <div><label>KM retour *</label><input type="number" value={formRetour.km_retour} onChange={e => setFormRetour(f => ({ ...f, km_retour: e.target.value }))} required /></div>
                <div><label>Date retour</label><input type="date" value={formRetour.date_retour} onChange={e => setFormRetour(f => ({ ...f, date_retour: e.target.value }))} /></div>
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginTop: 12 }}>Observations / État du véhicule</label>
                <textarea value={formRetour.observations} onChange={e => setFormRetour(f => ({ ...f, observations: e.target.value }))} rows={3}
                  placeholder="RAS ou noter les dommages, remarques…"
                  style={{ marginTop: 6, width: '100%', padding: 8, border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button className="btn-primary" onClick={handleSaveRetour} disabled={!formRetour.km_retour || saving}>
              {saving ? 'Enregistrement…' : '✅ Valider le retour'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Onglet Partenaires loueurs ───────────────────────────────────────────
  const renderLoueurs = () => {
    const loueursAffiches = loueurs.filter(l => filterConcession === 'all' || l.concession_id == null || String(l.concession_id) === filterConcession);
    return (
    <>
    {selectConcession && (
      <div className="page-toolbar">
        {selectConcession}
      </div>
    )}
    <div className="table-container">
      <table className="vc-table">
        <thead>
          <tr><th>Loueur</th>{!user?.concession_id && <th>Concession</th>}<th>Actions</th></tr>
        </thead>
        <tbody>
          {loueursAffiches.length === 0 && (
            <tr><td colSpan={user?.concession_id ? 2 : 3} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              Aucun loueur — <button className="vc-history-toggle" onClick={openAjouterLoueur}>Ajouter le premier</button>
            </td></tr>
          )}
          {loueursAffiches.map(l => (
            <tr key={l.id} className="vc-row">
              <td style={{ fontWeight: 600 }}>{l.nom}</td>
              {!user?.concession_id && <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{l.concession_nom || '— Toutes —'}</td>}
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="vc-btn-action" style={{ background: '#64748b', color: 'var(--text-inverse)' }} onClick={() => openEditerLoueur(l)}>✏️ Modifier</button>
                  <button className="vc-btn-action" style={{ background: 'var(--bg-muted)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }} onClick={() => handleDeleteLoueur(l)}>🗑️</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
    );
  };

  const renderModalLoueur = () => {
    if (!showModalLoueur) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editLoueur ? '✏️ Modifier le loueur' : '🤝 Nouveau loueur'}</h2>
            <button className="modal-close" onClick={() => setShowModalLoueur(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="form-section">
              <div className="form-row">
                <div>
                  <label>Nom du loueur *</label>
                  <input type="text" placeholder="ex : Hertz, Auto-Discount…" value={formLoueur.nom}
                    onChange={e => setFormLoueur(f => ({ ...f, nom: e.target.value }))} />
                </div>
              </div>
              {!user?.concession_id && (
                <div className="form-row">
                  <div>
                    <label>Concession</label>
                    <select value={formLoueur.concession_id} onChange={e => setFormLoueur(f => ({ ...f, concession_id: e.target.value }))}>
                      <option value="">— Toutes les concessions —</option>
                      {concessions.map(c => <option key={c.id} value={String(c.id)}>{c.nom}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowModalLoueur(false)}>Annuler</button>
            <button className="btn-primary" onClick={handleSaveLoueur} disabled={!formLoueur.nom.trim() || savingLoueur}>
              {savingLoueur ? 'Enregistrement…' : editLoueur ? '💾 Enregistrer' : '➕ Ajouter'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Modal documents d'un prêt (permis, CNI…) ─────────────────────────────
  // Accessible depuis n'importe quel prêt existant (interne OU location) — clé =
  // pret.id. À la création, les pièces sont saisies directement dans la modale
  // « Nouveau prêt » (buffer) ; cette modale-ci sert donc à la consultation/édition.
  const renderModalDocuments = () => {
    if (!docPret) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📎 Pièces jointes{docPret.label ? ` — ${docPret.label}` : ''}</h2>
            <button className="modal-close" onClick={() => setDocPret(null)}>✕</button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0 }}>
              Documents du dossier (permis, CNI, justificatifs). Stockage sécurisé, liens d'accès temporaires.
            </p>
            <CourtoisieDocsGrid
              fichiers={fichiers}
              pretId={docPret.id}
              onUpload={handleUploadFichier}
              onDelete={handleDeleteFichier}
              uploading={uploadingFichier}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setDocPret(null)}>Fermer</button>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU PRINCIPAL
  ═══════════════════════════════════════════════════════════════════════════ */

  if (loading) {
    return (
      <div className="vehicules-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Chargement…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vehicules-view" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12 }}>
        <div style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontSize: 16 }}>{error}</div>
        <button className="btn-primary" onClick={loadData}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* ── Onglets ──────────────────────────────────────────────────────── */}
      <div className="page-tabs">
        <button
          className={activeTab === 'prets' ? 'active' : ''}
          onClick={() => setActiveTab('prets')}
        >
          📋 Prêts & Statuts
        </button>
        <button
          className={activeTab === 'archives' ? 'active' : ''}
          onClick={() => setActiveTab('archives')}
        >
          🗄️ Archives
        </button>
        <button
          className={activeTab === 'flotte' ? 'active' : ''}
          onClick={() => setActiveTab('flotte')}
        >
          🚗 Flotte ({vehiculesActifs.length} véhicule{vehiculesActifs.length !== 1 ? 's' : ''})
        </button>
        <button
          className={activeTab === 'loueurs' ? 'active' : ''}
          onClick={() => setActiveTab('loueurs')}
        >
          🤝 Partenaires loueurs ({loueurs.length})
        </button>
      </div>

      {/* ── Contenu ──────────────────────────────────────────────────────── */}
      {activeTab === 'flotte' && renderFlotte()}
      {activeTab === 'prets' && renderPrets()}
      {activeTab === 'archives' && renderArchives()}
      {activeTab === 'loueurs' && renderLoueurs()}

      {/* ── Modaux ───────────────────────────────────────────────────────── */}
      {renderModalVehicule()}
      {renderModalDetail()}
      {renderModalNouveauPret()}
      {renderModalRetour()}
      {renderModalLoueur()}
      {renderModalDocuments()}
      {modaleDossier}
    </div>
  );
}

export default VehiculesCourtoisie;
